import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Input, Badge, Button,
         Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Chart from 'react-apexcharts';
import { useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { getCosts, getTrend, describeError, formatInr } from './purchasesApi';

const TrendBadge = ({ pct, piCount }) => {
  if (Number(piCount) < 2) return <Badge color="light" className="text-dark" pill>only one PI</Badge>;
  const n = Number(pct);
  if (!n) return <Badge color="secondary" pill>no change</Badge>;
  return <Badge color={n > 0 ? 'danger' : 'success'} pill>{n > 0 ? '+' : ''}{n}%</Badge>;
};

const CostList = () => {
  const [params, setParams] = useSearchParams();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(params.get('sku') || '');
  const [matchFilter, setMatchFilter] = useState('all');
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    getCosts()
      .then((d) => setCosts(d.costs))
      .catch((e) => setError(describeError(e, 'Could not load costs.')))
      .finally(() => setLoading(false));
  }, []);

  const openTrend = async (sku) => {
    setTrendLoading(true); setTrend({ styleSku: sku, series: [] });
    try { setTrend(await getTrend(sku)); }
    catch (e) { setTrend({ styleSku: sku, error: describeError(e) }); }
    finally { setTrendLoading(false); }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return costs.filter((c) => {
      if (matchFilter === 'matched' && !c.product_id) return false;
      if (matchFilter === 'unmatched' && c.product_id) return false;
      if (!q) return true;
      return [c.style_sku, c.size_sku, c.description, c.product_title, c.colour]
        .some((f) => String(f ?? '').toLowerCase().includes(q));
    });
  }, [costs, search, matchFilter]);

  // One series per size/colour, never an average: three styles genuinely differ
  // in price between their sizes, and averaging would hide exactly the anomaly
  // an admin opened this chart to find.
  const chartSeries = (trend?.series || []).map((s) => ({
    name: s.name,
    data: s.points.map((p) => [new Date(p.piDate).getTime(), Number(p.cost)]),
  }));
  const chartOptions = {
    chart: { type: 'line', height: 340, toolbar: { show: false } },
    // Stepline is the honest curve: a cost holds at the last invoice's price
    // until the next PI restates it. A straight line would imply a gradual drift
    // that never happened.
    stroke: { curve: 'stepline', width: 3 },
    colors: ['#8a5a44', '#b08968', '#6f6a63', '#c9a227'],
    markers: { size: 5 },
    xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
    yaxis: { labels: { formatter: (v) => `₹${Math.round(v)}` } },
    dataLabels: { enabled: false },
    legend: { position: 'top' },
    grid: { borderColor: '#eee' },
    tooltip: { x: { format: 'dd MMM yyyy' }, y: { formatter: (v) => formatInr(v) } },
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Purchases" title="Cost Trends" mainTitle="Cost Trends" />
      <Container fluid>
        <Row><Col sm="12"><Card>
          <CardHeader className="d-flex justify-content-between align-items-center py-3">
            <h5 className="mb-0">Current cost by product</h5>
            <div className="d-flex align-items-center gap-2">
              <Input type="search" value={search} placeholder="Search SKU, product or colour"
                     onChange={(e) => { setSearch(e.target.value); setParams({}); }}
                     style={{ width: 'auto', minWidth: 240 }} />
              <Input type="select" value={matchFilter} onChange={(e) => setMatchFilter(e.target.value)} style={{ width: 'auto' }}>
                <option value="all">All products ({costs.length})</option>
                <option value="matched">In catalogue ({costs.filter((c) => c.product_id).length})</option>
                <option value="unmatched">Not in catalogue ({costs.filter((c) => !c.product_id).length})</option>
              </Input>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? <div className="text-center p-4"><Spinner /></div>
             : error ? <div className="text-center p-4 text-danger">{error}</div>
             : rows.length === 0 ? <div className="text-center p-4 text-muted">Nothing matches this search.</div>
             : (
              <div className="table-responsive">
                <Table className="table-bordered" size="sm">
                  <thead>
                    <tr>
                      <th>Product</th><th>SKU</th><th>Size</th><th>Colour</th>
                      <th className="text-end">First cost</th><th className="text-end">Current cost</th>
                      <th>Latest PI</th><th className="text-end">Change</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 400).map((c, i) => (
                      <tr key={i}>
                        <td>
                          {c.product_title || c.description || <span className="text-muted">—</span>}
                          {!c.product_id && <Badge color="warning" pill className="ms-2">not in catalogue</Badge>}
                        </td>
                        <td><small>{c.style_sku}</small></td>
                        <td><small className="text-muted">{c.size_sku}</small></td>
                        <td><small className="text-muted">{c.colour || '—'}</small></td>
                        <td className="text-end">{formatInr(c.first_cost)}</td>
                        <td className="text-end"><strong>{formatInr(c.latest_cost)}</strong></td>
                        <td><small>{c.latest_pi_ref}<br /><span className="text-muted">{c.latest_pi_date}</span></small></td>
                        <td className="text-end"><TrendBadge pct={c.change_pct} piCount={c.pi_count} /></td>
                        <td className="text-end">
                          <Button size="sm" color="primary" outline onClick={() => openTrend(c.style_sku)}>Trend</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {rows.length > 400 && (
                  <p className="text-muted mt-2 mb-0">
                    <small>Showing the first 400 of {rows.length} — narrow the search to see the rest.</small>
                  </p>
                )}
              </div>
            )}
          </CardBody>
        </Card></Col></Row>
      </Container>

      <Modal isOpen={!!trend} toggle={() => setTrend(null)} centered size="lg">
        <ModalHeader toggle={() => setTrend(null)}>
          {trend?.description || trend?.styleSku} <small className="text-muted">— cost history</small>
        </ModalHeader>
        <ModalBody>
          {trendLoading ? <div className="text-center p-4"><Spinner /></div>
           : trend?.error ? <p className="text-danger mb-0">{trend.error}</p>
           : trend?.summary?.piCount === 1 ? (
             <div className="text-center p-4">
               <h4 className="mb-1">{formatInr(trend.summary.latest)}</h4>
               <p className="text-muted mb-0">Seen on only one PI, so there is no trend to plot yet.</p>
             </div>
           ) : chartSeries.length > 0 ? (
             <Fragment>
               <Row className="mb-3 text-center">
                 <Col><small className="text-muted d-block">First</small><strong>{formatInr(trend.summary.first)}</strong></Col>
                 <Col><small className="text-muted d-block">Current</small><strong>{formatInr(trend.summary.latest)}</strong></Col>
                 <Col><small className="text-muted d-block">Lowest</small><strong>{formatInr(trend.summary.min)}</strong></Col>
                 <Col><small className="text-muted d-block">Highest</small><strong>{formatInr(trend.summary.max)}</strong></Col>
                 <Col><small className="text-muted d-block">PIs</small><strong>{trend.summary.piCount}</strong></Col>
               </Row>
               <Chart options={chartOptions} series={chartSeries} type="line" height={340} />
             </Fragment>
           ) : null}
        </ModalBody>
      </Modal>
    </Fragment>
  );
};

export default CostList;
